import React from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Clock3,
  Copy,
  Github,
  RefreshCw,
} from 'lucide-react';

import { Button } from '@/Modules/Core/Components/shadcn/button';
import WebHookJsonViewer from './WebHookJsonViewer';

const getStatusTextClassName = (statusKey) => {
  if (statusKey === 'success') {
    return 'text-emerald-700 dark:text-emerald-300';
  }

  if (statusKey === 'failed') {
    return 'text-rose-700 dark:text-rose-300';
  }

  return 'text-foreground';
};

const WebHookLogDetailView = ({
  log,
  onBack,
  onCopyDeliveryId,
  onCopyPayload,
  onInvestigate,
  isRefreshing = false,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            className="border-border/40 bg-transparent hover:bg-accent/20"
            onClick={onBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to logs
          </Button>

          {isRefreshing ? (
            <span className="inline-flex items-center gap-2 rounded-full border border-border/35 bg-background/80 px-3 py-1.5 text-[12px] font-medium text-muted-foreground dark:bg-card/50">
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />
              Refreshing payload...
            </span>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-border/35 bg-background/80 hover:bg-muted/60 dark:bg-card/50 dark:hover:bg-background/60"
            onClick={onCopyDeliveryId}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy delivery ID
          </Button>

          {log.statusKey === 'failed' ? (
            <Button
              type="button"
              variant="outline"
              className="border-rose-400/35 bg-rose-500/10 text-rose-700 hover:bg-rose-500/15 dark:text-rose-200"
              onClick={onInvestigate}
            >
              <AlertTriangle className="mr-2 h-4 w-4" />
              Investigate
            </Button>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl border border-border/35 bg-background/90 p-5 dark:bg-card/25">
        <div className="space-y-3">
          <div className="inline-flex items-center rounded-full border border-border/35 bg-muted/70 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground dark:bg-background/45">
            {log.eventLabel}
          </div>

          <h2 className="text-[28px] font-semibold tracking-tight text-foreground">
            {log.authorLabel}
          </h2>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Github className="h-4 w-4" />
              {log.repoLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock3 className="h-4 w-4" />
              {log.createdAtLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Delivery ID
          </div>
          <div className="mt-2 break-all font-mono text-[14px] text-foreground">
            {log.deliveryId}
          </div>
        </div>

        <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Created Date
          </div>
          <div className="mt-2 text-[18px] font-semibold text-foreground">
            {log.createdAtLabel}
          </div>
        </div>

        <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Author
          </div>
          <div className="mt-2 text-[18px] font-semibold text-foreground">
            {log.authorLabel}
          </div>
        </div>

        <div className="rounded-2xl border border-border/35 bg-background/90 px-5 py-4 dark:bg-card/25">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Status
          </div>
          <div className={`mt-2 text-[18px] font-semibold capitalize ${getStatusTextClassName(log.statusKey)}`}>
            {log.statusLabel}
          </div>
        </div>
      </div>

      <WebHookJsonViewer payloadText={log.payloadText} onCopy={onCopyPayload} />
    </div>
  );
};

export default WebHookLogDetailView;
